import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MdChevronRight } from "react-icons/md";
import InputField from "components/fields/InputField";
import { ReadOnlyChip } from "components/role/RoleSwitcher";
import { suspendedAccessMessage, useAuth } from "contexts/AuthContext";
import { apiGet, resendMfa } from "lib/apiClient";
import {
  getRememberedSeat,
  groupSeatsByOrganisation,
  parseSeatParam,
  rememberSeat,
  roleTheme,
  seatHomePath,
  seatKey,
} from "lib/seats";

const RESEND_COOLDOWN_SECONDS = 30;

function mfaErrorMessage(err) {
  switch (err?.code) {
    case "MFA_CODE_INVALID":
      return "That code is not right. Check it and try again.";
    case "MFA_CODE_EXPIRED":
      return "That code has expired. Send a new one and try again.";
    case "MFA_RATE_LIMITED":
      return "Codes were requested too often. Wait a minute, then try again.";
    default:
      return err?.message || "Could not verify the code.";
  }
}

function MfaStep({ challenge, onChallengeChange, onRestart, onVerified }) {
  const { verifyMfa } = useAuth();
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  async function handleVerify(e) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await verifyMfa(challenge.challengeToken, code);
      onVerified(result);
    } catch (err) {
      if (suspendedAccessMessage(err?.code)) {
        onRestart(suspendedAccessMessage(err.code));
        return;
      }
      if (err?.code === "MFA_CHALLENGE_INVALID") {
        onRestart("Your sign-in timed out. Sign in again to get a new code.");
        return;
      }
      if (err?.code === "MFA_TOO_MANY_ATTEMPTS") {
        onRestart("Too many wrong codes. Sign in again to get a new code.");
        return;
      }
      setError(mfaErrorMessage(err));
      setSubmitting(false);
    }
  }

  async function handleResend() {
    setError(null);
    setNotice(null);
    setResending(true);
    try {
      const next = await resendMfa(challenge.challengeToken);
      onChallengeChange({ ...challenge, ...next });
      setCode("");
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setNotice(`A new code is on its way to ${next.destinationHint || challenge.destinationHint}.`);
    } catch (err) {
      if (err?.code === "MFA_CHALLENGE_INVALID" || err?.code === "MFA_TOO_MANY_ATTEMPTS") {
        onRestart("Your sign-in timed out. Sign in again to get a new code.");
        return;
      }
      setError(mfaErrorMessage(err));
    } finally {
      setResending(false);
    }
  }

  const channelLabel = challenge.channel === "SMS" ? "by SMS" : "by email";

  return (
    <div>
      <h4 className="mb-1.5 text-2xl font-semibold tracking-tight text-foreground">Enter your code</h4>
      <p className="mb-6 text-sm text-muted-foreground">
        We sent a 6-digit code {channelLabel} to <span className="font-semibold text-navy-700 dark:text-white">{challenge.destinationHint}</span>. It expires in 10 minutes.
      </p>
      <form onSubmit={handleVerify} noValidate>
        <InputField
          variant="auth"
          extra="mb-3"
          label="Verification code*"
          placeholder="123456"
          id="mfa-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        />
        {error && <p className="mb-4 text-sm font-medium text-red-500">{error}</p>}
        {notice && <p className="mb-4 text-sm font-medium text-green-600">{notice}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="mt-2 inline-flex h-10 w-full items-center justify-center rounded-sm bg-primary px-6 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-60"
        >
          {submitting ? "Verifying…" : "Verify and sign in"}
        </button>
      </form>
      <div className="mt-4 flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={handleResend}
          disabled={resending || cooldown > 0}
          className="font-medium text-link hover:underline disabled:cursor-not-allowed disabled:text-gray-400"
        >
          {resending ? "Sending…" : cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
        </button>
        <button type="button" onClick={() => onRestart(null)} className="font-medium text-gray-600 hover:text-brand-500">
          Start over
        </button>
      </div>
    </div>
  );
}

export function RoleChooser({ seats, onChosen }) {
  const { switchSeat } = useAuth();
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState(null);
  const groups = groupSeatsByOrganisation(seats);

  async function choose(seat) {
    setError(null);
    if (seat.active) {
      rememberSeat(seat);
      onChosen(seat);
      return;
    }
    setBusyKey(seatKey(seat));
    try {
      await switchSeat(seat);
    } catch (err) {
      setBusyKey(null);
      setError(err?.message || "Could not switch to that role.");
    }
  }

  return (
    <div>
      <h4 className="mb-1.5 text-2xl font-semibold tracking-tight text-foreground">Continue as...</h4>
      <p className="mb-6 text-sm text-muted-foreground">
        You hold more than one role. Choose the one to work in now. You can switch any time from the role badge at the top of the portal.
      </p>
      <div className="flex flex-col gap-5">
        {groups.map((group) => (
          <div key={group.tenantId}>
            <p className="mb-2 text-xs font-medium text-muted-foreground">{group.tenantName}</p>
            <ul className="flex flex-col gap-2">
              {group.seats.map((seat) => {
                const key = seatKey(seat);
                const theme = roleTheme(seat.kind);
                return (
                  <li key={key}>
                    <button
                      type="button"
                      disabled={Boolean(busyKey)}
                      onClick={() => choose(seat)}
                      className={`flex w-full items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 text-left transition-colors disabled:cursor-wait ${theme.chooser}`}
                    >
                      <span className={`h-3 w-3 shrink-0 rounded-full ${theme.dot}`} aria-hidden="true" />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className={`truncate text-sm font-semibold ${theme.text}`}>{seat.roleLabel}</span>
                          {!seat.canManage && <ReadOnlyChip />}
                        </span>
                        <span className="block truncate text-sm text-navy-700 dark:text-white">
                          {seat.kind === "PARTNER" ? `${seat.partnerName} (partner of ${seat.tenantName})` : seat.tenantName}
                        </span>
                      </span>
                      {busyKey === key ? (
                        <span className="shrink-0 text-xs text-gray-500">Opening…</span>
                      ) : (
                        <MdChevronRight className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      {error && <p className="mt-4 text-sm font-medium text-red-500">{error}</p>}
    </div>
  );
}

export default function SignIn() {
  const { login, authNotice, clearAuthNotice } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [seatFromLink] = useState(() => {
    const seat = parseSeatParam(searchParams.get("seat"));
    if (seat) rememberSeat(seat);
    return seat;
  });
  const [seatUsed, setSeatUsed] = useState(null);
  const [chooserSeats, setChooserSeats] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(() => authNotice);
  const [submitting, setSubmitting] = useState(false);
  const [challenge, setChallenge] = useState(null);

  useEffect(() => {
    if (authNotice) setError(authNotice);
  }, [authNotice]);

  async function afterSignIn(result, seat) {
    if (result?.mustChangePassword) {
      navigate("/auth/set-password", { replace: true });
      return;
    }
    if (seat) {
      navigate(seatHomePath(seat.kind), { replace: true });
      return;
    }
    const held = await apiGet("/portal/seats").catch(() => null);
    const seats = held?.seats ?? [];
    if (seats.length > 1) {
      setChallenge(null);
      setChooserSeats(seats);
      return;
    }
    navigate("/portal", { replace: true });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    clearAuthNotice();

    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Enter a valid email address.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await login(email, password, getRememberedSeat());
      setSeatUsed(result?.seatUsed ?? null);
      if (result?.mfaRequired) {
        setChallenge(result);
        setSubmitting(false);
        return;
      }
      await afterSignIn(result, result?.seatUsed ?? null);
    } catch (err) {
      setError(suspendedAccessMessage(err?.code) || err.message || "Sign in failed.");
      setSubmitting(false);
    }
  }

  function restart(message) {
    setChallenge(null);
    setPassword("");
    setError(message);
  }

  return (
    <div className="flex w-full">
      <div className="w-full">
        {chooserSeats ? (
          <RoleChooser seats={chooserSeats} onChosen={(seat) => navigate(seatHomePath(seat.kind), { replace: true })} />
        ) : challenge ? (
          <MfaStep challenge={challenge} onChallengeChange={setChallenge} onRestart={restart} onVerified={(result) => afterSignIn(result, seatUsed)} />
        ) : (
          <>
            <h4 className="mb-1.5 text-2xl font-semibold tracking-tight text-foreground">Portal Sign In</h4>
            <p className="mb-6 text-sm text-muted-foreground">For tenant and partner staff. New here? Use the email and temporary password from your welcome email.</p>
            {seatFromLink && (
              <p className={`-mt-3 mb-6 flex items-center gap-2 text-sm font-medium ${roleTheme(seatFromLink.kind).text}`}>
                <span className={`h-2.5 w-2.5 rounded-full ${roleTheme(seatFromLink.kind).dot}`} aria-hidden="true" />
                You will sign in to the {seatFromLink.kind === "PARTNER" ? "partner" : "tenant"} role you picked in the admin portal.
              </p>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <InputField
                variant="auth"
                extra="mb-3"
                label="Email*"
                placeholder="you@yourcompany.com"
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <InputField
                variant="auth"
                extra="mb-3"
                label="Password*"
                placeholder="Min. 10 characters"
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <div className="mb-4 flex justify-end">
                <Link to="/auth/forgot-password" className="text-sm font-medium text-link hover:underline">
                  Forgot password?
                </Link>
              </div>

              {error && <p className="mb-4 text-sm font-medium text-red-500">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="mt-2 inline-flex h-10 w-full items-center justify-center rounded-sm bg-primary px-6 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-60"
              >
                {submitting ? "Signing in…" : "Sign In"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
